/**
 * useDictation - Diktierfunktion für Notizfelder.
 *
 * Modus-Priorität:
 *  1. "whisper": OpenAI Whisper über das Backend (sobald OPENAI_API_KEY hinterlegt ist,
 *     wird dieser Modus automatisch aktiv - beste Qualität, alle Browser).
 *  2. "browser": Web Speech API (kostenlos, kein Key nötig; Chrome, Edge, Safari).
 *  3. "none": Weder Key noch Browser-Unterstützung vorhanden.
 *
 * status: "idle" | "recording" | "processing"
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const getSpeechRecognition = () =>
  typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;

export function useDictation({ onTranscript, onError } = {}) {
  const [whisperAvailable, setWhisperAvailable] = useState(false);
  const [status, setStatus] = useState("idle");
  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const finalsRef = useRef("");
  const onTranscriptRef = useRef(onTranscript);
  const onErrorRef = useRef(onError);
  onTranscriptRef.current = onTranscript;
  onErrorRef.current = onError;

  useEffect(() => {
    let mounted = true;
    api.get("/voice/status")
      .then((r) => { if (mounted) setWhisperAvailable(Boolean(r.data?.available)); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const browserSupported = Boolean(getSpeechRecognition());
  const mode = whisperAvailable ? "whisper" : browserSupported ? "browser" : "none";

  const cleanupStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const stop = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) { /* noop */ }
      recognitionRef.current = null;
      setStatus("idle");
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop(); // onstop übernimmt Transkription + Status
    }
  }, []);

  const startBrowser = useCallback(() => {
    const SR = getSpeechRecognition();
    if (!SR) return;
    const rec = new SR();
    rec.lang = "de-DE";
    rec.continuous = true;
    rec.interimResults = true;
    finalsRef.current = "";
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalsRef.current += t;
        else interim += t;
      }
      onTranscriptRef.current?.((finalsRef.current + interim).trim());
    };
    rec.onerror = (e) => {
      recognitionRef.current = null;
      setStatus("idle");
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        onErrorRef.current?.("Mikrofon-Zugriff wurde verweigert. Bitte in den Browser-Einstellungen erlauben.");
      } else if (e.error !== "aborted" && e.error !== "no-speech") {
        onErrorRef.current?.("Diktieren fehlgeschlagen. Bitte erneut versuchen oder Text eingeben.");
      }
    };
    rec.onend = () => {
      recognitionRef.current = null;
      setStatus("idle");
    };
    recognitionRef.current = rec;
    rec.start();
    setStatus("recording");
  }, []);

  const startWhisper = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      mr.onstop = async () => {
        cleanupStream();
        setStatus("processing");
        try {
          const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
          const fd = new FormData();
          fd.append("file", blob, "notiz.webm");
          const r = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (r.data?.text) onTranscriptRef.current?.(r.data.text.trim());
          else onErrorRef.current?.(r.data?.message || "Transkription fehlgeschlagen. Bitte Text eingeben.");
        } catch (e) {
          onErrorRef.current?.("Transkription fehlgeschlagen. Bitte erneut versuchen oder Text eingeben.");
        } finally {
          mediaRecorderRef.current = null;
          setStatus("idle");
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setStatus("recording");
    } catch (e) {
      cleanupStream();
      setStatus("idle");
      onErrorRef.current?.("Mikrofon-Zugriff wurde verweigert. Bitte in den Browser-Einstellungen erlauben.");
    }
  }, []);

  const start = useCallback(() => {
    if (status !== "idle") return;
    if (mode === "whisper") startWhisper();
    else if (mode === "browser") startBrowser();
    else onErrorRef.current?.("Diktieren wird von diesem Browser nicht unterstützt. Bitte Chrome, Edge oder Safari verwenden – oder Text eingeben.");
  }, [status, mode, startWhisper, startBrowser]);

  // Aufräumen beim Unmount
  useEffect(() => () => {
    if (recognitionRef.current) { try { recognitionRef.current.abort(); } catch (e) { /* noop */ } }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      try { mediaRecorderRef.current.stop(); } catch (e) { /* noop */ }
    }
    cleanupStream();
  }, []);

  return { mode, status, start, stop };
}
